import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { db, auth } from '../firebaseConfig';
import { collection, query, orderBy, where, getDocs, addDoc, serverTimestamp, updateDoc, deleteDoc, doc, limit } from 'firebase/firestore';
import emailToName from '../utils/emailToName';
import Holidays from 'date-holidays';

const hd = new Holidays('KR');

function getHolidayName(date: Date): string | null {
  const h = hd.isHoliday(date);
  if (h && h.length > 0) return h[0].name;
  return null;
}
function isHolidayOrSunday(date: Date): boolean {
  return !!getHolidayName(date) || date.getDay() === 0;
}

function formatDate(date: Date) {
  const y = date.getFullYear();
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const d = date.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const getMMDD = (date: Date) => {
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const d = date.getDate().toString().padStart(2, '0');
  return `${m}-${d}`;
};

const ScheduleEditModal = ({ open, onClose, onSave, defaultValue, date }: { open: boolean, onClose: () => void, onSave: (data: any) => void, defaultValue?: any, date: Date }) => {
  const [title, setTitle] = useState(defaultValue?.title ? defaultValue.title.replace(/^\d{2}-\d{2} /, '') : '');
  const [content, setContent] = useState(defaultValue?.content || '');
  const [selectedColor, setSelectedColor] = useState(defaultValue?.color || '#d32f2f');
  const [loading, setLoading] = useState(false);
  
  // 색상 옵션들
  const colorOptions = [
    { name: '빨간색', value: '#d32f2f', bg: '#fff5f5', border: '#fed7d7' },
    { name: '파란색', value: '#1976d2', bg: '#f0f8ff', border: '#b3d9ff' },
    { name: '초록색', value: '#2e7d32', bg: '#f0fff4', border: '#9ae6b4' },
    { name: '보라색', value: '#7b1fa2', bg: '#faf5ff', border: '#d6bcfa' },
    { name: '주황색', value: '#ed6c02', bg: '#fffaf0', border: '#fbd38d' },
    { name: '회색', value: '#666666', bg: '#f8f9fa', border: '#e2e8f0' }
  ];
  
  useEffect(() => {
    setTitle(defaultValue?.title ? defaultValue.title.replace(/^\d{2}-\d{2} /, '') : '');
    setContent(defaultValue?.content || '');
    setSelectedColor(defaultValue?.color || '#d32f2f');
  }, [defaultValue, open, date]);
  
  const mmdd = getMMDD(date);
  const handleSubmit = async () => {
    if (!title.trim()) return;
    setLoading(true);
    await onSave({ title: `${mmdd} ${title}`, content, color: selectedColor });
    setLoading(false);
    setTitle(''); setContent('');
    setSelectedColor('#d32f2f');
    onClose();
  };
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 10000 }}>
      <div style={{ background: '#fff', borderRadius: 12, minWidth: 400, maxWidth: 600, minHeight: 220, boxShadow: '0 2px 16px rgba(0,0,0,0.18)', padding: 32, position: 'absolute', left: '50%', top: '20%', transform: 'translate(-50%, 0)' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 18, right: 18, fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        <div style={{ fontWeight: 700, fontSize: 20, marginBottom: 18, color: '#1976d2' }}>{defaultValue ? '일정 수정' : '일정 등록'}</div>
        <div style={{ marginBottom: 12, fontWeight: 600, color: '#444', fontSize: 16 }}>날짜: {formatDate(date)}</div>
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: 'red', minWidth: 54 }}>{mmdd}</span>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="제목" style={{ flex: 1, fontSize: 16, padding: 8, borderRadius: 4, border: '1px solid #bbb', marginBottom: 0 }} />
        </div>
        <textarea value={content} onChange={e => setContent(e.target.value)} placeholder="내용" style={{ width: '100%', fontSize: 15, padding: 8, borderRadius: 4, border: '1px solid #bbb', minHeight: 60, marginBottom: 12 }} />
        
        {/* 색상 선택 */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#444', marginBottom: 8 }}>일정 색상 선택:</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {colorOptions.map((color) => (
              <button
                key={color.value}
                onClick={() => setSelectedColor(color.value)}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: selectedColor === color.value ? '3px solid #333' : '2px solid #ddd',
                  backgroundColor: color.bg,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                title={color.name}
              >
                <div style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: color.value,
                  border: '1px solid #fff'
                }} />
              </button>
            ))}
          </div>
        </div>
        
        <button style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 22px', fontWeight: 600, fontSize: 16, marginTop: 8, cursor: 'pointer' }} onClick={handleSubmit} disabled={loading}>{loading ? '저장중...' : (defaultValue ? '저장' : '등록')}</button>
      </div>
    </div>
  );
};

const PAGE_SIZE = 5;

const ScheduleListModal = ({ open, onClose, schedules, date, onEdit, onDelete }: { open: boolean, onClose: () => void, schedules: any[], date: Date, onEdit: (schedule: any) => void, onDelete: (id: string) => void }) => {
  const user = auth.currentUser;
  if (!open) return null;
  const handleEditClick = (s: any) => {
    onClose();
    setTimeout(() => { onEdit(s); }, 0);
  };
  return (
    <div style={{ position: 'fixed', left: 0, top: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 10001 }}>
      <div style={{ background: '#fff', borderRadius: 12, minWidth: 400, maxWidth: 600, minHeight: 220, boxShadow: '0 2px 16px rgba(0,0,0,0.18)', padding: 32, position: 'absolute', left: '50%', top: '20%', transform: 'translate(-50%, 0)' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 18, right: 18, fontSize: 20, background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
        <div style={{ fontWeight: 700, fontSize: 20, color: '#1976d2', marginBottom: 18 }}>{formatDate(date)} 일정</div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: 350, overflowY: 'auto' }}>
          {schedules.length === 0 ? (
            <li style={{ color: '#888', textAlign: 'center', margin: 30 }}>등록된 일정이 없습니다.</li>
          ) : (
            schedules.map(s => {
              const mmddMatch = s.title.match(/^\d{2}-\d{2}/);
              const titleRest = mmddMatch ? s.title.slice(mmddMatch[0].length).trim() : s.title;
              return (
                <li key={s.id} style={{ borderBottom: '1px solid #eee', padding: '14px 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 2 }}>
                      {mmddMatch && <span style={{ color: '#444', marginRight: 4 }}>{mmddMatch[0]}</span>}
                      <span style={{ color: s.color || '#d32f2f' }}>{titleRest}</span>
                    </div>
                    <div style={{ color: '#444', fontSize: 14, margin: '4px 0 2px 0', whiteSpace: 'pre-line' }}>{s.content}</div>
                    <div style={{ marginTop: 2 }}>
                      <span style={{ color: '#222', fontSize: 14, fontWeight: 700 }}>{emailToName[s.authorEmail] || s.authorEmail}</span>
                    </div>
                  </div>
                  {s.authorEmail === auth.currentUser?.email && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <button style={{ background: '#fff', color: '#1976d2', border: '1px solid #1976d2', borderRadius: 4, padding: '2px 10px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }} onClick={() => handleEditClick(s)}>수정</button>
                      <button style={{ background: '#fff', color: '#d32f2f', border: '1px solid #d32f2f', borderRadius: 4, padding: '2px 10px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }} onClick={() => onDelete(s.id)}>삭제</button>
                    </div>
                  )}
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
};

const ScheduleBoardPage = () => {
  const [schedules, setSchedules] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [showEdit, setShowEdit] = useState(false);
  const [editSchedule, setEditSchedule] = useState<any>(null);
  const [showRegister, setShowRegister] = useState(false);
  const [registerDate, setRegisterDate] = useState<Date | null>(null);
  const [filterMine, setFilterMine] = useState(false);
  const user = auth.currentUser;
  const [showListModal, setShowListModal] = useState(false);
  const [listModalDate, setListModalDate] = useState<Date | null>(null);
  const [listModalSchedules, setListModalSchedules] = useState<any[]>([]);

  // 전체 일정 불러오기 (최신순)
  const fetchSchedules = async () => {
    const q = query(collection(db, 'schedules'), orderBy('date', 'desc'), orderBy('createdAt', 'desc'));
    const snap = await getDocs(q);
    setSchedules(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  // 최초 mount 시 전체 일정 불러오기
  useEffect(() => {
    fetchSchedules();
  }, []);

  // 날짜별 일정 필터링 (캘린더 셀, 등록/수정용)
  const schedulesForDate = (date: Date) => {
    const d = formatDate(date);
    return schedules.filter(s => s.date === d && (!filterMine || s.authorEmail === user?.email));
  };

  // 달력 셀에 일정 미리보기 (제목만, MM-DD 제거, 3개까지 모두 보이고 그 이상은 +N개 더보기)
  const tileContent = ({ date, view }: { date: Date, view: string }) => {
    if (view === 'month') {
      const holidayName = getHolidayName(date);
      const daySchedules = schedulesForDate(date);
      return (
        <div style={{ 
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-start',
          padding: '4px 0'
        }}>
          {holidayName && (
            <span style={{ 
              color: '#d32f2f', 
              fontSize: 12, 
              fontWeight: 700, 
              marginBottom: '2px',
              width: '100%',
              textAlign: 'center'
            }}>
              {holidayName}
            </span>
          )}
          {daySchedules && daySchedules.length > 0 && (
            <div style={{ 
              width: '100%', 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column',
              gap: '1px'
            }}>
              {daySchedules.slice(0, 5).map((s, idx) => {
                const titleWithoutDate = s.title.replace(/^(\d{2}-\d{2})\s*/, '');
                const authorName = emailToName[s.authorEmail] || s.authorEmail.split('@')[0];
                const displayText = titleWithoutDate.length > 8 ? titleWithoutDate.substring(0, 8) + '...' : titleWithoutDate;
                
                // 색상 매핑
                const colorMap = {
                  '#d32f2f': { bg: '#fff5f5', border: '#fed7d7' },
                  '#1976d2': { bg: '#f0f8ff', border: '#b3d9ff' },
                  '#2e7d32': { bg: '#f0fff4', border: '#9ae6b4' },
                  '#7b1fa2': { bg: '#faf5ff', border: '#d6bcfa' },
                  '#ed6c02': { bg: '#fffaf0', border: '#fbd38d' },
                  '#666666': { bg: '#f8f9fa', border: '#e2e8f0' }
                };
                const scheduleColor = s.color || '#d32f2f';
                const colorStyle = colorMap[scheduleColor as keyof typeof colorMap] || colorMap['#d32f2f'];
                
                return (
                  <div key={idx} style={{ 
                    fontSize: 11, 
                    color: scheduleColor, 
                    fontWeight: 600, 
                    padding: '1px 2px',
                    borderRadius: '2px',
                    backgroundColor: colorStyle.bg,
                    border: `1px solid ${colorStyle.border}`,
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '1.1'
                  }}>
                    <span style={{ color: '#666', fontSize: 10 }}>{authorName}</span>
                    <br />
                    <span>{displayText}</span>
                  </div>
                );
              })}
              {daySchedules.length > 5 && (
                <div style={{ 
                  fontSize: 10, 
                  color: '#888', 
                  textAlign: 'center',
                  padding: '1px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '2px'
                }}>
                  +{daySchedules.length - 5}개 더
                </div>
              )}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  const tileClassName = ({ date, view }: { date: Date, view: string }) => {
    if (view === 'month' && isHolidayOrSunday(date)) {
      return 'calendar-holiday';
    }
    return '';
  };

  // 일정 등록
  const handleRegister = async (data: any) => {
    if (!registerDate) return;
    const user = auth.currentUser;
    await addDoc(collection(db, 'schedules'), {
      title: data.title,
      content: data.content,
      color: data.color || '#d32f2f',
      date: formatDate(registerDate),
      createdAt: serverTimestamp(),
      authorId: user?.uid || '',
      authorEmail: user?.email || '',
    });
    setSelectedDate(registerDate);
    await fetchSchedules();
    setShowRegister(false);
  };

  // 일정 수정
  const handleEdit = (schedule: any) => {
    setEditSchedule(schedule);
    setShowEdit(true);
    setRegisterDate(new Date(schedule.date));
  };
  const handleEditSave = async (data: any) => {
    if (!editSchedule || !registerDate) return;
    await updateDoc(doc(db, 'schedules', editSchedule.id), {
      title: data.title,
      content: data.content,
      color: data.color || '#d32f2f',
    });
    setShowEdit(false);
    setEditSchedule(null);
    await fetchSchedules();
  };

  // 일정 삭제
  const handleDelete = async (id: string) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    await deleteDoc(doc(db, 'schedules', id));
    await fetchSchedules();
  };

  // 달력 날짜 클릭 시 해당 날짜의 일정 모달로 보여주기
  const handleDayClick = (date: Date) => {
    setListModalDate(date);
    setListModalSchedules(schedulesForDate(date));
    setShowListModal(true);
  };

  return (
    <>
      <style>{`
        /* 달력 전용 스타일 - 글로벌 CSS 영향 방지 */
        .schedule-board-calendar .react-calendar {
          width: 100% !important;
          max-width: 100%;
          min-height: 800px;
          background: #fff;
          border: none;
          font-family: inherit;
        }
        .schedule-board-calendar .react-calendar__navigation {
          margin-bottom: 1rem;
        }
        .schedule-board-calendar .react-calendar__navigation button {
          min-width: 44px;
          background: none;
          font-size: 18px;
          margin-top: 8px;
          color: #1976d2;
          font-weight: 600;
        }
        .schedule-board-calendar .react-calendar__navigation button:enabled:hover,
        .schedule-board-calendar .react-calendar__navigation button:enabled:focus {
          background-color: #e6f3ff;
          border-radius: 6px;
        }
        .schedule-board-calendar .react-calendar__navigation button[disabled] {
          background-color: #f0f0f0;
          color: #666;
        }
        .schedule-board-calendar .react-calendar__month-view__weekdays {
          text-align: center;
          text-transform: uppercase;
          font-weight: bold;
          font-size: 0.75em;
          color: #666;
          display: grid !important;
          grid-template-columns: repeat(7, 1fr) !important;
          width: 100% !important;
        }
        .schedule-board-calendar .react-calendar__month-view__weekdays__weekday {
          padding: 0.5em;
          text-align: center !important;
          width: 100% !important;
        }
        .schedule-board-calendar .react-calendar__month-view__weekdays__weekday abbr {
          text-decoration: none;
          font-size: 16px;
          font-weight: 600;
          color: #666;
        }
        .schedule-board-calendar .react-calendar__month-view__days__day {
          min-width: 80px !important;
          padding: 0.75em 0.5em;
          background: none;
          text-align: center !important;
          line-height: 16px;
          font-size: 16px;
          color: #333;
          font-weight: 500;
          width: 100% !important;
        }
        .schedule-board-calendar .react-calendar__month-view__days {
          display: grid !important;
          grid-template-columns: repeat(7, 1fr) !important;
          gap: 1px;
          width: 100% !important;
        }
        .schedule-board-calendar .react-calendar__month-view__days__day--neighboringMonth {
          color: #ccc;
        }
        .schedule-board-calendar .react-calendar__tile {
          height: 160px !important;
          max-width: 100%;
          padding: 6px 4px !important;
          background: none;
          text-align: left;
          line-height: 1.2;
          font-size: 16px !important;
          color: #333;
          font-weight: 500;
          border: none;
          border-radius: 6px;
          display: flex !important;
          flex-direction: column !important;
          align-items: flex-start !important;
          justify-content: flex-start !important;
        }
        .schedule-board-calendar .react-calendar__tile:enabled:hover,
        .schedule-board-calendar .react-calendar__tile:enabled:focus {
          background-color: #e6f3ff;
          color: #1976d2;
        }
        .schedule-board-calendar .react-calendar__tile--now {
          background: #ffff76;
          color: #333;
        }
        .schedule-board-calendar .react-calendar__tile--now:enabled:hover,
        .schedule-board-calendar .react-calendar__tile--now:enabled:focus {
          background: #ffffa9;
        }
        .schedule-board-calendar .react-calendar__tile--hasActive {
          background: #76baff;
          color: white;
        }
        .schedule-board-calendar .react-calendar__tile--hasActive:enabled:hover,
        .schedule-board-calendar .react-calendar__tile--hasActive:enabled:focus {
          background: #a9d4ff;
        }
        .schedule-board-calendar .react-calendar__tile--active {
          background: #e3f0ff !important;
          color: #1976d2 !important;
        }
        .schedule-board-calendar .react-calendar__tile--active:enabled:hover,
        .schedule-board-calendar .react-calendar__tile--active:enabled:focus {
          background: #cbe3ff !important;
          color: #1976d2 !important;
        }
        .schedule-board-calendar .react-calendar--selectRange .react-calendar__tile--hover {
          background-color: #e6e6e6;
        }
        .schedule-board-calendar .react-calendar__tile abbr {
          font-size: 16px !important;
          font-weight: 500 !important;
          color: inherit !important;
          text-decoration: none !important;
          line-height: 16px !important;
        }
        .schedule-board-calendar .react-calendar__tile button {
          background: none !important;
          border: none !important;
          padding: 0 !important;
          margin: 0 !important;
          font-size: 16px !important;
          font-weight: 500 !important;
          color: inherit !important;
          line-height: 16px !important;
          width: 100% !important;
          height: auto !important;
        }
        .calendar-holiday {
          color: #d32f2f !important;
          font-weight: 700;
        }
      `}</style>
      <div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', padding: '24px clamp(20px, 4vw, 48px)' }}>
        {/* 전체화면 달력으로 변경 - 제목 제거하여 공간 확보 */}
        <div style={{ 
          background: '#fff', 
          borderRadius: 16, 
          boxShadow: '0 2px 16px rgba(0,0,0,0.10)', 
          padding: 32, 
          width: '100%', 
          maxWidth: '100%', 
          margin: 0,
          minHeight: 800 
        }}>
          <div className="schedule-board-calendar" style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'stretch', 
            width: '100%' 
          }}>
            <Calendar
              onChange={date => setSelectedDate(date as Date)}
              value={selectedDate}
              tileContent={tileContent}
              tileClassName={tileClassName}
              onClickDay={handleDayClick}
              calendarType="gregory"
              locale="ko-KR"
            />
          </div>
        </div>
        {/* 일정 등록/수정 모달 */}
        <ScheduleEditModal open={showRegister} onClose={() => setShowRegister(false)} onSave={handleRegister} date={registerDate || new Date()} />
        <ScheduleEditModal open={showEdit} onClose={() => { setShowEdit(false); setEditSchedule(null); }} onSave={handleEditSave} defaultValue={editSchedule} date={registerDate || new Date()} />
        {/* 일정 목록 모달 */}
        <ScheduleListModal open={showListModal} onClose={() => setShowListModal(false)} schedules={listModalSchedules} date={listModalDate || new Date()} onEdit={handleEdit} onDelete={handleDelete} />
      </div>
    </>
  );
};

export default ScheduleBoardPage; 